import { ApplicationFormAnswers } from "../../components/ApplicationForm/ApplicationFormContext"

/**
 * Maps the required details and provided answers from Clearscore+Startline to a new ApplicationFormAnswers object.
 * Maps drivingLicence to UNKNOWN
 * Maps maritalStatus to UNKNOWN
 * Maps only the first employment entry and defaults lengthOfTime to 2 years for employmentHistory
 * If you're employed, 
 *    - Takes the value from the job title field, 
 *    - Maps companyName to UNKNOWN
 *    - Maps location to their current residential townCity
 * 
 * @param {Record<string, any>} answers - The answers object containing various details.
 * @param {boolean} isJobTitleRequired - A flag indicating whether the job title is required.
 * @returns {ApplicationFormAnswers} - The mapped ApplicationFormAnswers object.
 */
export const mapRequiredDetails = (answers: Record<string, any>, isJobTitleRequired: boolean): ApplicationFormAnswers => ({
  addressHistory: mapAddressHistory(answers),
  contactNumber: answers.requiredDetails.contactNumber,
  drivingLicence: 'UNKNOWN',
  emailAddress: answers.emailAddress,
  employmentHistory: mapEmploymentHistory(answers, isJobTitleRequired),
  loanAmount: answers.loanAmount,
  maritalStatus: 'UNKNOWN',
  monthlyIncome: answers.monthlyIncome,
  personalDetails: {
    dateOfBirth: answers.personalDetails.dateOfBirth,
    firstName: answers.personalDetails.firstName,
    surname: answers.personalDetails.surname
  },
  title: answers.title,
  vehicleType: answers.vehicleType
})

/**
 * Maps the address history from the provided answers, updating the address situation for each entry.
 * 
 * @param {Record<string, any>} answers - The answers object containing various details.
 * @returns {Object} - The updated address history.
 */
export const mapAddressHistory = (answers: Record<string, any>) => {
  // note entries aren't zero-indexed
  const firstAddressSituation = answers.addressHistory.entries['1'].addressSituation
  const updatedAddressHistory = updateAddressHistory(answers.addressHistory.entries, firstAddressSituation)
  answers.addressHistory.entries = updatedAddressHistory
  return { ...answers.addressHistory }
}

/**
 * Updates the address history by setting the address situation of each entry to the first address situation.
 * 
 * @param {Record<string, any>} addressHistory - The address history entries to be updated.
 * @param {string} firstAddressSituation - The address situation of the first entry.
 * @returns {Record<string, any>} - The updated address history entries with the new address situation.
 */
export const updateAddressHistory = (addressHistory: any, firstAddressSituation: string) => Object.keys(addressHistory).reduce((acc, key) => {
  acc[key] = {
    ...addressHistory[key],
    addressSituation: firstAddressSituation
  }
  return acc
}, {} as Record<string, typeof addressHistory['1']>)


/**
 * Maps the employment history based on the provided answers and a flag 'isCurrentStatusEmployed' indicating to include the job title in the mapping returning only the first employment entry with the following modifications:
 * Maps only the first employment entry and defaults lengthOfTime to 2 years
 * If you're unemployed, ('isCurrentStatusEmployed' false) it does no further mapping
 * If you're employed ('isCurrentStatusEmployed' true), 
 *    - Takes the value from the job title field, 
 *    - Maps companyName to UNKNOWN
 *    - Maps location to their current residential townCity
 * @param {Record<string, any>} answers - The record containing the answers, including employment and address history
 * @param {boolean} isCurrentStatusEmployed - A flag indicating whether to include the job title in the mapping
 * @returns {Record<string, any>} The mapped employment history with modified details
 */
export const mapEmploymentHistory = (answers: Record<string, any>, isCurrentStatusEmployed: boolean) => {
  const updateEmployerDetailsForEmployedStatuses = (jobTitle: string, location: string): Record<string, any> => ({
    jobTitle,
    companyName: 'UNKNOWN',
    location
  })

  const firstEmploymentEntry = answers.employmentHistory.entries['1']
  const firstResidentialTown = answers.addressHistory.entries['1'].addressDetails.town

  if (isCurrentStatusEmployed) {
    firstEmploymentEntry.employerDetails = updateEmployerDetailsForEmployedStatuses(
      answers.requiredDetails?.jobTitle,
      firstResidentialTown
    )
  }

  firstEmploymentEntry.lengthOfTime = { years: '2', months: '0' }
  return { entries: { '1': { ...firstEmploymentEntry } } }
}